"use client";

import { ImagePreviewDialog } from "@cmp-src/data-display/ImagePreviewDialog";
import { AttachmentTile } from "@cmp-src/upload/AttachmentTile";
import { cn } from "@gettactic/helpers";
import { useMemo, useState } from "react";
import type { PendingAttachment } from "@/features/workplace-requests/hooks/usePendingAttachments";

interface CommentAttachmentsTrayProps {
  attachments: PendingAttachment[];
  onRemove: (id: string) => void;
  /** Re-runs a failed upload. Failed tiles are clickable only when provided. */
  onRetry?: (id: string) => void;
  className?: string;
}

/**
 * The files staged for a comment, shown as a wrapping row of tiles above the
 * text. Caps its own height so a bulk drop scrolls instead of pushing the
 * composer over the conversation.
 *
 * Staged images open in the same lightbox as posted ones — the preview reads off
 * the local object URL, so it works before the upload has finished.
 */
export const CommentAttachmentsTray: React.FC<CommentAttachmentsTrayProps> = ({
  attachments,
  onRemove,
  onRetry,
  className
}) => {
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);

  // Only the previewable ones are in the lightbox, so its paging matches what
  // the viewer can actually see.
  const previews = useMemo(
    () =>
      attachments.flatMap((attachment) =>
        attachment.previewUrl
          ? [
              {
                id: attachment.id,
                url: attachment.previewUrl,
                filename: attachment.filename
              }
            ]
          : []
      ),
    [attachments]
  );

  if (attachments.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        "flex max-h-44 flex-wrap gap-2 overflow-y-auto pt-2.5 pb-1",
        className
      )}
    >
      {attachments.map((attachment) => {
        const previewPosition = previews.findIndex(
          (preview) => preview.id === attachment.id
        );

        return (
          <AttachmentTile
            key={attachment.id}
            filename={attachment.filename}
            url={attachment.previewUrl}
            byteSize={attachment.byteSize}
            status={attachment.status}
            errorMessage={attachment.errorMessage}
            onRemove={() => onRemove(attachment.id)}
            onRetry={onRetry ? () => onRetry(attachment.id) : undefined}
            onOpen={
              previewPosition >= 0
                ? () => setPreviewIndex(previewPosition)
                : undefined
            }
          />
        );
      })}

      <ImagePreviewDialog
        images={previews}
        index={previewIndex}
        onIndexChange={setPreviewIndex}
        onClose={() => setPreviewIndex(null)}
      />
    </div>
  );
};
